import { useEffect, useMemo, useState } from 'react';
import { useBrandKit, useUpdateBrandKit, type BrandKitWritable } from '../../api/brand-kit.api';
import { useTemplates, usePreviewTemplate } from '../../api/templates.api';
import { useToast } from '../../components/ui/Toast';
import { ImageUploadField } from '../../components/ui/ImageUploadField';

/* ── Settings · Brand kit ──
   One row per org. Recruiters set the defaults that cascade into every
   template; per-template overrides on top live in the builder's settings
   panel. The logo is uploaded (JT1-1922); a pasted link still works. */

const FONT_CHOICES = ['Montserrat', 'Inter', 'Arial', 'Georgia', 'system-ui'];
const WIDTH_CHOICES = [560, 600, 640];

export function BrandKitSection() {
  const { data, isLoading } = useBrandKit();
  const updateMut = useUpdateBrandKit();
  const { addToast } = useToast();

  const { data: tmplData } = useTemplates({ kind: 'CONTENT_TEMPLATE', include_system: true });
  const previewMut = usePreviewTemplate();
  const [previewHtml, setPreviewHtml] = useState('');

  // Local edit state mirrors the server kit; reset on data load.
  const [draft, setDraft] = useState<BrandKitWritable>({});
  useEffect(() => {
    if (data?.data) setDraft({});
  }, [data?.data]);

  const current = useMemo(() => ({ ...(data?.data ?? {}), ...draft }), [data, draft]);

  const previewTemplate = useMemo(() => {
    const systemFirst = tmplData?.data.find((t) => t.is_system && t.blocks);
    return systemFirst ?? tmplData?.data.find((t) => t.blocks);
  }, [tmplData]);

  // Re-render the preview whenever the brand-kit draft changes.
  useEffect(() => {
    if (!previewTemplate?.blocks) return;
    const t = setTimeout(() => {
      previewMut.mutate(
        {
          blocks: previewTemplate.blocks!,
          brand_kit_override: draft as Record<string, unknown>,
          sample_id: 'candidate_sample',
        },
        { onSuccess: (out) => setPreviewHtml(out.html) },
      );
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, previewTemplate]);

  if (isLoading) return <Shell><p>Loading brand kit…</p></Shell>;

  const handleSave = async () => {
    try {
      await updateMut.mutateAsync(draft);
      setDraft({});
      addToast({ variant: 'success', title: 'Brand kit saved' });
    } catch {
      addToast({ variant: 'danger', title: 'Save failed' });
    }
  };

  const set = <K extends keyof BrandKitWritable>(k: K, v: BrandKitWritable[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const dirty = Object.keys(draft).length > 0;

  return (
    <Shell>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--color-text-brand, #1e1b4b)' }}>
            Brand kit
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--color-text-muted, #6b6b7d)' }}>
            Defaults applied to every new template. Per-template overrides live in the builder's settings panel.
          </p>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={!dirty || updateMut.isPending}
          data-testid="brand-kit-save"
          style={{
            padding: '8px 16px',
            background: dirty ? 'var(--color-action-primary, #1e1b4b)' : '#e3e3ee',
            color: dirty ? '#fff' : '#9a9aab',
            border: 'none',
            borderRadius: 6,
            fontFamily: 'inherit',
            fontSize: 13,
            fontWeight: 600,
            cursor: dirty ? 'pointer' : 'not-allowed',
          }}
        >
          {updateMut.isPending ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
        </button>
      </header>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 360px', gap: 24 }}>
        <div>
          <Field label="Logo">
            <ImageUploadField
              value={current.logo_url}
              onChange={(url) => set('logo_url', url)}
              inputStyle={input()}
              testId="brand-kit-logo"
            />
          </Field>
          <Field label="Company name (resolves to {{company.name}})">
            <input
              type="text"
              value={current.company_name ?? ''}
              onChange={(e) => set('company_name', e.target.value || null)}
              placeholder="Bridge Talent"
              style={input()}
            />
          </Field>
          <Field label="Company address (resolves to {{company.address}})">
            <input
              type="text"
              value={current.company_address ?? ''}
              onChange={(e) => set('company_address', e.target.value || null)}
              style={input()}
            />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <ColorField label="Primary" value={current.primary_color ?? '#1e1b4b'} onChange={(v) => set('primary_color', v)} />
            <ColorField label="Accent" value={current.accent_color ?? '#3b5bdb'} onChange={(v) => set('accent_color', v)} />
            <ColorField label="Background" value={current.background_color ?? '#f4f5f9'} onChange={(v) => set('background_color', v)} />
            <ColorField label="Text" value={current.text_color ?? '#2b2b3c'} onChange={(v) => set('text_color', v)} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Heading font">
              <select value={current.heading_font ?? 'Montserrat'} onChange={(e) => set('heading_font', e.target.value)} style={input()}>
                {FONT_CHOICES.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </Field>
            <Field label="Body font">
              <select value={current.body_font ?? 'Montserrat'} onChange={(e) => set('body_font', e.target.value)} style={input()}>
                {FONT_CHOICES.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label={`Button radius: ${current.button_radius_px ?? 8}px`}>
              <input
                type="range"
                min={0}
                max={16}
                value={current.button_radius_px ?? 8}
                onChange={(e) => set('button_radius_px', Number(e.target.value))}
              />
            </Field>
            <Field label="Content width (px)">
              <select value={current.content_width_px ?? 600} onChange={(e) => set('content_width_px', Number(e.target.value))} style={input()}>
                {WIDTH_CHOICES.map((w) => <option key={w} value={w}>{w}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Footer HTML (appended into the auto-injected footer)">
            <textarea
              value={current.footer_html ?? ''}
              onChange={(e) => set('footer_html', e.target.value || null)}
              rows={3}
              style={{ ...input(), fontFamily: 'var(--font-mono, ui-monospace, monospace)' }}
            />
          </Field>
        </div>

        <aside style={{ position: 'sticky', top: 0, alignSelf: 'flex-start' }}>
          <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.04, color: 'var(--color-text-muted)', marginBottom: 8 }}>
            Preview (applied to "{previewTemplate?.name ?? '-'}")
          </p>
          <div
            style={{
              border: '1px solid var(--color-border-default, #ececf2)',
              borderRadius: 8,
              height: 460,
              overflow: 'hidden',
              background: 'var(--color-surface-muted, #f7f8fc)',
            }}
          >
            {previewHtml ? (
              <iframe
                title="Brand kit preview"
                srcDoc={previewHtml}
                // Same iframe-sandbox guard as the builder + gallery
                // previews — keeps rendered template hrefs from navigating
                // the preview back into the Engage app.
                sandbox=""
                // The preview is display-only. Without this, clicking inside
                // the sandboxed iframe (e.g. a button/link in the rendered
                // template) blanks the frame and the in-progress brand edits
                // appear to vanish. Disabling pointer events makes the preview
                // inert so clicks never reach it.
                style={{ width: '100%', height: '100%', border: 0, pointerEvents: 'none' }}
              />
            ) : (
              <p style={{ padding: 16, color: 'var(--color-text-muted)' }}>Loading preview…</p>
            )}
          </div>
        </aside>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <section
      style={{
        background: 'var(--card-bg, #fff)',
        border: '1px solid var(--card-border, #ececf2)',
        borderRadius: 'var(--card-radius, 8px)',
        boxShadow: 'var(--card-shadow, none)',
        padding: 24,
      }}
    >
      {children}
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted, #6b6b7d)', textTransform: 'uppercase', letterSpacing: 0.04 }}>{label}</span>
      {children}
    </label>
  );
}

function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <Field label={label}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} style={{ width: 36, height: 30, padding: 0, border: '1px solid var(--color-border-default, #ececf2)', borderRadius: 4 }} />
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)} style={{ ...input(), flex: 1 }} />
      </div>
    </Field>
  );
}

function input(): React.CSSProperties {
  return {
    padding: '6px 10px',
    border: '1px solid var(--color-border-default, #ececf2)',
    borderRadius: 4,
    fontSize: 13,
    fontFamily: 'inherit',
  };
}
